import { BellOutlined, UserOutlined, ClockCircleOutlined, SendOutlined, FileTextOutlined, CheckOutlined, EyeOutlined, NotificationOutlined } from '@ant-design/icons'
import { Badge, Dropdown, List, Spin, Empty, Typography, Space, Tag, Tooltip, Modal, Button, Card, message, Drawer, Select, Input } from 'antd'
import React, { useEffect, useState } from 'react'
import { notifyUserService } from '@/pages/workspace/notify/service'
import type { UserUnreadNotify } from '@/pages/workspace/notify/types'
import { PRIORITY_LEVELS, MESSAGE_TYPES } from '@/pages/workspace/notify/types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'
import './index.less'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const { Text, Paragraph } = Typography

/**
 * 消息提醒组件
 * 显示未读消息数量和最新未读消息列表
 */
const NotifyBell: React.FC = () => {
  const [unreadCount, setUnreadCount] = useState(0)
  const [unreadList, setUnreadList] = useState<UserUnreadNotify[]>([])
  const [loading, setLoading] = useState(false)
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedNotify, setSelectedNotify] = useState<UserUnreadNotify | null>(null)
  
  // 全部消息抽屉相关状态
  const [allMessagesDrawerVisible, setAllMessagesDrawerVisible] = useState(false)
  const [allMessagesList, setAllMessagesList] = useState<UserUnreadNotify[]>([])
  const [allMessagesLoading, setAllMessagesLoading] = useState(false)
  const [allMessagesPagination, setAllMessagesPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [searchParams, setSearchParams] = useState<{
    keyword?: string
    messageType?: number
    priority?: number
    readStatus?: boolean
  }>({
    keyword: undefined,
    messageType: undefined,
    priority: undefined,
    readStatus: undefined,
  })

  // 获取未读消息数量
  const fetchUnreadCount = async () => {
    try {
      const response = await notifyUserService.getUnreadCount()
      if (response.code === 0 && response.data) {
        setUnreadCount(response.data || 0)
      }
    } catch (error) {
      console.error('获取未读消息数量失败:', error)
    }
  }

  // 获取未读消息列表
  const fetchUnreadList = async () => {
    setLoading(true)
    try {
      const response = await notifyUserService.getUnreadNotifyList({
        pageSize: 5,
        current: 1,
      })
      if (response.code === 0 && response.data) {
        setUnreadList(response.data || [])
      }
    } catch (error) {
      console.error('获取未读消息列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 标记消息为已读
  const handleMarkAsRead = async (notifyMsgId: string) => {
    try {
      const response = await notifyUserService.markNotifyAsRead(notifyMsgId)
      if(response.code === 0) {
        message.success('已标记为已读')
        // 刷新未读消息列表和数量
        await Promise.all([fetchUnreadCount(), fetchUnreadList()])
      } else {
        message.error(response.message)
      }
    } catch (error) {
      console.error('标记已读失败:', error)
    }
  }

  // 点击消息项
  const handleNotifyClick = async (notify: UserUnreadNotify) => {
    // 设置选中的消息
    setSelectedNotify(notify)
    // 打开弹窗
    setModalVisible(true)
    // 关闭下拉框
    setDropdownVisible(false)
    
    // 立即标记为已读（如果未读）
    if (notify.notifyMsgId && !notify.readStatus) {
      await handleMarkAsRead(notify.notifyMsgId)
    }
  }

  // 关闭弹窗
  const handleModalClose = () => {
    setModalVisible(false)
    setSelectedNotify(null)
  }

  // 查看全部消息（从弹窗打开抽屉）
  const handleViewAllFromModal = () => {
    setModalVisible(false)
    setSelectedNotify(null)
    setAllMessagesDrawerVisible(true)
    fetchAllMessages()
  }

  // 查看全部（从下拉框打开抽屉）
  const handleViewAll = () => {
    setDropdownVisible(false)
    setAllMessagesDrawerVisible(true)
    fetchAllMessages()
  }

  // 获取所有消息列表
  const fetchAllMessages = async (params?: any) => {
    setAllMessagesLoading(true)
    try {
      const requestParams = {
        ...searchParams,
        current: params?.current || allMessagesPagination.current,
        pageSize: params?.pageSize || allMessagesPagination.pageSize,
        ...params,
      }

      const response = await notifyUserService.getMyNotifyList(requestParams)
      if (response.code === 0 && response.data) {
        setAllMessagesList(response.data.list || [])
        setAllMessagesPagination({
          current: requestParams.current,
          pageSize: requestParams.pageSize,
          total: response.data.total || 0,
        })
      }
    } catch (error) {
      console.error('获取消息列表失败:', error)
      message.error('获取消息列表失败')
    } finally {
      setAllMessagesLoading(false)
    }
  }

  // 筛选条件变化
  const handleFilterChange = (field: string, value: any) => {
    const newParams = { ...searchParams, [field]: value }
    setSearchParams(newParams)
    fetchAllMessages({ ...newParams, current: 1 })
  }

  // 关键词搜索
  const handleKeywordSearch = (value: string) => {
    const newParams = { ...searchParams, keyword: value || undefined }
    setSearchParams(newParams)
    fetchAllMessages({ ...newParams, current: 1 })
  }

  // 关闭全部消息抽屉
  const handleAllMessagesDrawerClose = () => {
    setAllMessagesDrawerVisible(false)
    // 刷新未读消息数量
    fetchUnreadCount()
  }

  // 从列表项打开详情
  const handleOpenDetailFromList = async (notify: UserUnreadNotify) => {
    setSelectedNotify(notify)
    setModalVisible(true)
    
    // 如果未读，标记为已读
    if (!notify.readStatus) {
      await handleMarkAsRead(notify.notifyMsgId)
      // 刷新列表
      fetchAllMessages()
    }
  }

  // 一键已读
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) {
      message.info('暂无未读消息')
      return
    }

    try {
      const response = await notifyUserService.markAllAsRead();
      if(response.code === 0) {
        message.success('已全部标记为已读')
        // 刷新未读消息列表和数量
        await Promise.all([fetchUnreadCount(), fetchUnreadList()])
      } else {
        message.error(response.message)
      }
    } catch (error) {
      console.error('一键已读失败:', error)
      message.error('操作失败，请重试')
    }
  }

  // 获取优先级标签
  const getPriorityTag = (priority: number) => {
    switch (priority) {
      case PRIORITY_LEVELS.URGENT:
        return <Tag color="red">紧急</Tag>
      case PRIORITY_LEVELS.IMPORTANT:
        return <Tag color="orange">重要</Tag>
      case PRIORITY_LEVELS.NORMAL:
        return <Tag color="blue">普通</Tag>
      case PRIORITY_LEVELS.MINOR:
        return <Tag color="default">次要</Tag>
      default:
        return null
    }
  }

  // 获取消息类型标签
  const getMessageTypeTag = (messageType: number) => {
    switch (messageType) {
      case MESSAGE_TYPES.NOTIFY:
        return <Tag color="blue">通知</Tag>
      case MESSAGE_TYPES.ANNOUNCEMENT:
        return <Tag color="green">公告</Tag>
      default:
        return null
    }
  }

  // 下拉菜单内容
  const dropdownContent = (
    <div className="notify-bell-dropdown">
      <div className="notify-bell-dropdown-header">
        <div className="notify-bell-dropdown-header-left">
          <Text strong>未读消息</Text>
          {unreadCount > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              共 {unreadCount} 条
            </Text>
          )}
        </div>
        {unreadCount > 0 && (
          <Button 
            type="link" 
            size="small" 
            icon={<CheckOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              handleMarkAllAsRead()
            }}
            style={{ padding: '0 4px' }}
          >
            一键已读
          </Button>
        )}
      </div>
      <div className="notify-bell-dropdown-content">
        {loading ? (
          <div className="notify-bell-loading">
            <Spin />
          </div>
        ) : unreadList.length === 0 ? (
          <div className="notify-bell-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无未读消息"
              style={{ padding: '20px 0 12px' }}
            />
            <Button 
              type="primary" 
              onClick={handleViewAll}
              style={{ marginBottom: 16 }}
            >
              查看全部消息
            </Button>
          </div>
        ) : (
          <List
            dataSource={unreadList}
            renderItem={(item) => (
              <List.Item
                className="notify-bell-list-item"
                onClick={() => handleNotifyClick(item)}
              >
                <div className="notify-bell-item-content">
                  <div className="notify-bell-item-header">
                    <Space size={4}>
                      {getPriorityTag(item.priority)}
                      {getMessageTypeTag(item.messageType)}
                    </Space>
                    {item.publishTime && (
                      <Tooltip title={dayjs(item.publishTime).format('YYYY-MM-DD HH:mm:ss')}>
                        <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
                          {dayjs(item.publishTime).fromNow()}
                        </Text>
                      </Tooltip>
                    )}
                  </div>
                  <div className="notify-bell-item-title">
                    <Text strong ellipsis={{ tooltip: item.title }}>
                      {item.title}
                    </Text>
                  </div>
                  {item.content && (
                    <div className="notify-bell-item-content-text">
                      <Paragraph
                        type="secondary"
                        ellipsis={{ rows: 2, tooltip: item.content }}
                        style={{ marginBottom: 0 }}
                      >
                        {item.content}
                      </Paragraph>
                    </div>
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
      {unreadList.length > 0 && (
        <div className="notify-bell-dropdown-footer">
          <a onClick={handleViewAll}>查看全部</a>
        </div>
      )}
    </div>
  )

  // 下拉框显示时加载数据
  const handleVisibleChange = (visible: boolean) => {
    setDropdownVisible(visible)
    if (visible) {
      fetchUnreadList()
    }
  }

  // 组件挂载时获取未读消息数量
  useEffect(() => {
    fetchUnreadCount()
    // 设置定时器，每30秒刷新一次未读消息数量
    const timer = setInterval(() => {
      fetchUnreadCount()
    }, 30000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <>
      <Dropdown
        open={dropdownVisible}
        onOpenChange={handleVisibleChange}
        dropdownRender={() => dropdownContent}
        placement="bottomRight"
        trigger={['click']}
        overlayClassName="notify-bell-dropdown-overlay"
      >
        <div className="notify-bell-icon">
          <Badge count={unreadCount} offset={[-2, 2]} size="small">
            <BellOutlined style={{ fontSize: 18 }} />
          </Badge>
        </div>
      </Dropdown>

      <Modal
        title={null}
        open={modalVisible}
        onCancel={handleModalClose}
        width={720}
        footer={null}
        className="notify-bell-modal"
        bodyStyle={{ padding: 0 }}
        style={{ maxHeight: 'calc(100vh - 100px)' }}
      >
        {selectedNotify && (
          <div className="notify-bell-modal-wrapper">
            {/* 头部区域 */}
            <div className="notify-bell-modal-header">
              <div className="notify-bell-modal-header-title">
                <Text strong style={{ fontSize: 18, color: '#262626' }}>
                  {selectedNotify.title}
                </Text>
              </div>
            </div>

            {/* 元信息卡片 */}
            <div className="notify-bell-modal-body">
              <Card size="small" className="notify-bell-modal-meta-card">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <div className="notify-bell-modal-meta-item">
                    <UserOutlined className="notify-bell-modal-meta-icon" />
                    <Space size={4}>
                      <Text type="secondary" style={{ fontSize: 13 }}>发布人</Text>
                      <Text strong style={{ fontSize: 13 }}>
                        {selectedNotify.publisherNickName || selectedNotify.publisherUserName}
                      </Text>
                    </Space>
                  </div>
                  <div className="notify-bell-modal-meta-item">
                    <ClockCircleOutlined className="notify-bell-modal-meta-icon" />
                    <Space size={4}>
                      <Text type="secondary" style={{ fontSize: 13 }}>发布时间</Text>
                      <Text strong style={{ fontSize: 13 }}>
                        {dayjs(selectedNotify.publishTime).format('YYYY-MM-DD HH:mm:ss')}
                      </Text>
                    </Space>
                  </div>
                  {selectedNotify.pushPlatforms && (
                    <div className="notify-bell-modal-meta-item">
                      <SendOutlined className="notify-bell-modal-meta-icon" />
                      <Space size={4}>
                        <Text type="secondary" style={{ fontSize: 13 }}>推送平台</Text>
                        <Tag color="blue" style={{ fontSize: 12 }}>
                          {selectedNotify.pushPlatforms}
                        </Tag>
                      </Space>
                    </div>
                  )}
                  <div className="notify-bell-modal-meta-item">
                    <FileTextOutlined className="notify-bell-modal-meta-icon" />
                    <Space size={8} wrap>
                      <Text type="secondary" style={{ fontSize: 13 }}>消息类型</Text>
                      {getMessageTypeTag(selectedNotify.messageType)}
                      {getPriorityTag(selectedNotify.priority)}
                    </Space>
                  </div>
                </Space>
              </Card>

              {/* 消息内容卡片 */}
              {selectedNotify.content && (
                <Card 
                  size="small" 
                  className="notify-bell-modal-content-card"
                  title={
                    <Space size={6}>
                      <FileTextOutlined style={{ color: '#1890ff' }} />
                      <Text strong style={{ fontSize: 14 }}>消息内容</Text>
                    </Space>
                  }
                >
                  <div className="notify-bell-modal-content-text">
                    <Paragraph 
                      style={{ 
                        marginBottom: 0, 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.8,
                        fontSize: 14,
                        color: '#595959',
                      }}
                    >
                      {selectedNotify.content}
                    </Paragraph>
                  </div>
                </Card>
              )}
            </div>

            {/* 底部操作区 */}
            <div className="notify-bell-modal-footer">
              <Button type="link" onClick={handleViewAllFromModal}>
                查看全部消息
              </Button>
              <Button type="primary" onClick={handleModalClose}>
                我知道了
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 全部消息抽屉 */}
      <Drawer
        title={
          <Space>
            <span>全部消息</span>
            <Tag color="blue">{allMessagesPagination.total} 条</Tag>
          </Space>
        }
        placement="right"
        onClose={handleAllMessagesDrawerClose}
        open={allMessagesDrawerVisible}
        width={800}
        extra={
          <Space wrap>
            <Input.Search
              placeholder="搜索消息标题或内容"
              style={{ width: 200 }}
              size="small"
              allowClear
              value={searchParams.keyword}
              onSearch={handleKeywordSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  handleKeywordSearch('')
                }
              }}
            />
            <Select
              placeholder="消息类型"
              style={{ width: 100 }}
              size="small"
              allowClear
              value={searchParams.messageType}
              onChange={(value) => handleFilterChange('messageType', value)}
              options={[
                { label: '通知', value: MESSAGE_TYPES.NOTIFY },
                { label: '公告', value: MESSAGE_TYPES.ANNOUNCEMENT },
              ]}
            />
            <Select
              placeholder="优先级"
              style={{ width: 100 }}
              size="small"
              allowClear
              value={searchParams.priority}
              onChange={(value) => handleFilterChange('priority', value)}
              options={[
                { label: '紧急', value: PRIORITY_LEVELS.URGENT },
                { label: '重要', value: PRIORITY_LEVELS.IMPORTANT },
                { label: '普通', value: PRIORITY_LEVELS.NORMAL },
                { label: '次要', value: PRIORITY_LEVELS.MINOR },
              ]}
            />
            <Select
              placeholder="状态"
              style={{ width: 100 }}
              size="small"
              allowClear
              value={searchParams.readStatus}
              onChange={(value) => handleFilterChange('readStatus', value)}
              options={[
                { label: '未读', value: false },
                { label: '已读', value: true },
              ]}
            />
          </Space>
        }
      >
        <Spin spinning={allMessagesLoading}>
          <List
            dataSource={allMessagesList}
            renderItem={(item) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: 0,
                  border: 'none',
                  marginBottom: 12,
                }}
                onClick={() => handleOpenDetailFromList(item)}
              >
                <Card
                  size="small"
                  hoverable
                  style={{
                    width: '100%',
                    borderLeft: !item.readStatus ? '4px solid #1890ff' : '4px solid #52c41a',
                    backgroundColor: !item.readStatus ? '#f6f9ff' : '#fff',
                    transition: 'all 0.3s',
                  }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {/* 标题行 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space size={8}>
                        <NotificationOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                        <Text strong style={{ fontSize: 15 }}>
                          {item.title}
                        </Text>
                      </Space>
                      {!item.readStatus ? (
                        <Badge status="processing" text="未读" />
                      ) : (
                        <Badge status="success" text="已读" />
                      )}
                    </div>

                    {/* 消息内容 */}
                    {item.content && (
                      <div style={{ 
                        padding: '8px 12px', 
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 4,
                        borderLeft: '2px solid #e8e8e8',
                      }}>
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ marginBottom: 0, fontSize: 13, lineHeight: 1.6 }}
                        >
                          {item.content}
                        </Paragraph>
                      </div>
                    )}

                    {/* 元信息行 */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 8,
                    }}>
                      <Space size={8} wrap>
                        {getPriorityTag(item.priority)}
                        {getMessageTypeTag(item.messageType)}
                      </Space>
                      <Space size={12} wrap>
                        <Space size={4}>
                          <UserOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.publisherNickName || item.publisherUserName}
                          </Text>
                        </Space>
                        <Space size={4}>
                          <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                          <Tooltip title={dayjs(item.publishTime).format('YYYY-MM-DD HH:mm:ss')}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {dayjs(item.publishTime).fromNow()}
                            </Text>
                          </Tooltip>
                        </Space>
                        <Button
                          type="link"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenDetailFromList(item)
                          }}
                          style={{ padding: '0 4px', height: 'auto' }}
                        >
                          查看详情
                        </Button>
                      </Space>
                    </div>
                  </Space>
                </Card>
              </List.Item>
            )}
            pagination={{
              current: allMessagesPagination.current,
              pageSize: allMessagesPagination.pageSize,
              total: allMessagesPagination.total,
              onChange: (current, pageSize) => {
                fetchAllMessages({ current, pageSize })
              },
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条消息`,
              size: 'small',
            }}
          />
        </Spin>
      </Drawer>
    </>
  )
}

export default NotifyBell

